import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { paymentBankApi } from '../../api/paymentBank.js';
import { reportsApi } from '../../api/reports.js';
import { ErrorBox } from '../../components/ui.js';
import { money } from '../../lib/format.js';
import { ApiError } from '../../api/client.js';

/**
 * 엑셀 오류 체크 — 원본 payment/bank_excel_check.php + bank_excel_check_exec.php.
 *
 * 은행에서 받은 처리 결과 엑셀(.xlsx/.xls)을 브라우저에서 바로 읽어(SheetJS),
 * 서버가 내려준 "미완료 입금건" 목록(계좌번호·금액)과 대조한다. 일치하는 건만
 * 골라 check_excel='OK' 로 찍는다 — 원본이 stats=0 화면에서 감추던 방식 그대로다.
 */

interface ParsedRow {
  date: string;
  banksn: string;
  amount: string;
  matched: boolean;
}

export function BankExcelCheckPage() {
  const [corp, setCorp] = useState('bnncompany');
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [dragging, setDragging] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const meta = useQuery({
    queryKey: ['report', 'payment-bank', 'meta'],
    queryFn: () => reportsApi.meta('payment-bank'),
    staleTime: 5 * 60 * 1000,
  });
  const corpOptions = meta.data?.filters.find((f) => f.key === 'corp')?.options ?? [
    { value: 'bnncompany', label: 'bnncompany' },
  ];

  const pending = useQuery({
    queryKey: ['payment-bank', 'excel-check', 'pending', corp],
    queryFn: () => paymentBankApi.excelPending(corp),
    enabled: false,
  });

  const apply = useMutation({
    mutationFn: (items: { banksn: string; amount: string }[]) => paymentBankApi.excelApply(items),
  });

  const matchedCount = useMemo(() => rows.filter((r) => r.matched).length, [rows]);

  function reset() {
    setFileName('');
    setRows([]);
    setParseError(null);
    apply.reset();
    if (fileInput.current) fileInput.current.value = '';
  }

  async function processFile(file: File) {
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setParseError('엑셀 파일(.xlsx, .xls)만 업로드할 수 있습니다.');
      return;
    }
    setParseError(null);
    setFileName(file.name);

    try {
      const pendingResult = await pending.refetch();
      const dbRows = pendingResult.data ?? [];

      // xlsx(SheetJS)는 꽤 무거워서, 이 화면에 들어올 때만 받도록 동적 import 로 뺐다.
      const { read, utils } = await import('xlsx');
      const buf = await file.arrayBuffer();
      const workbook = read(buf, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]!];
      const table = utils.sheet_to_json<unknown[]>(sheet!, { header: 1, raw: false });

      const parsed: ParsedRow[] = [];
      for (const line of table) {
        if (!Array.isArray(line) || line.length < 3) continue;
        const excelSn = line[1] != null ? String(line[1]).trim().replace(/-/g, '') : '';
        const excelAmt = line[2] != null ? String(line[2]).replace(/,/g, '').trim() : '';
        if (!excelSn || !excelAmt || Number.isNaN(Number(excelAmt))) continue;

        const matched = dbRows.some((db) => db.banksn === excelSn && Number(db.amount) === Number(excelAmt));
        parsed.push({ date: line[0] != null ? String(line[0]) : '', banksn: excelSn, amount: excelAmt, matched });
      }

      setRows(parsed);
      if (parsed.length === 0) setParseError('유효한 데이터가 없습니다.');
    } catch {
      setRows([]);
      setParseError('파일 처리 중 오류가 발생했습니다.');
    }
  }

  function submit() {
    const items = rows.filter((r) => r.matched).map((r) => ({ banksn: r.banksn, amount: r.amount }));
    if (items.length === 0) return;
    apply.mutate(items);
  }

  return (
    <section>
      <header className="page-head">
        <div>
          <Link className="link link--back" to="/reports/payment-bank">
            ← 입금건 관리
          </Link>
          <h1 className="page-title">엑셀 오류 체크</h1>
          <p className="page-desc">
            은행에서 받은 처리 결과 엑셀을 올리면, 미완료 입금건(계좌번호·금액)과 자동 대조합니다.
          </p>
        </div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--ghost btn--sm" onClick={reset}>
            초기화
          </button>
        </div>
      </header>

      <div className="filters">
        <label className="field">
          <span className="field__label">법인</span>
          <select className="input" value={corp} onChange={(e) => { setCorp(e.target.value); reset(); }}>
            {corpOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div
        className={dragging ? 'drop-zone drop-zone--active' : 'drop-zone'}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const f = e.dataTransfer.files[0];
          if (f) void processFile(f);
        }}
        onClick={() => fileInput.current?.click()}
      >
        <input
          ref={fileInput}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void processFile(f);
          }}
        />
        <span className="drop-zone__msg">
          {fileName ? fileName : '엑셀 파일을 이곳에 드래그하거나 클릭하세요.'}
        </span>
      </div>

      {parseError && <ErrorBox error={new Error(parseError)} />}
      {apply.isError && (
        <ErrorBox
          error={apply.error instanceof ApiError ? apply.error : new Error('처리에 실패했습니다.')}
        />
      )}

      {rows.length > 0 && (
        <>
          <p className="hint">
            일치 <strong style={{ color: 'var(--ok)' }}>{matchedCount}건</strong> / 불일치{' '}
            <strong style={{ color: 'var(--danger)' }}>{rows.length - matchedCount}건</strong>
          </p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>날짜(엑셀)</th>
                  <th>계좌번호(엑셀)</th>
                  <th style={{ textAlign: 'right' }}>금액(엑셀)</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{r.date}</td>
                    <td>{r.banksn}</td>
                    <td className="td-right">{money(Number(r.amount))}</td>
                    <td>
                      {r.matched ? (
                        <span className="badge">OK</span>
                      ) : (
                        <span className="badge badge--danger">NG</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {matchedCount > 0 && (
            <button
              type="button"
              className="btn btn--primary btn--block"
              style={{ marginTop: 14 }}
              disabled={apply.isPending}
              onClick={submit}
            >
              {apply.isPending
                ? '처리 중…'
                : apply.isSuccess
                  ? `${apply.data.count}건 처리 완료`
                  : `${matchedCount}건 검증 완료 및 상태 업데이트`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
